import { store, store$ } from '@redux/store';
import { filter, fromEvent, map, merge } from 'rxjs';
import { segmentPageView, segmentTrack, segmentIdentify } from 'features/segment';
import HousingProductType from 'constants/housingProductType';
import { selectModal, selectFetchingModal } from 'layout/redux/selectors';
import typeahead$ from '../typeahead';

const breadcrumbItems = Array.from(document.querySelectorAll('[data-breadcrumb-item]'));
const searchSubmitElement = document.querySelector('[data-search-submit]');
const inputSearchBox = document.getElementById('blog_typeahead_input');
const isListingForYouSection = (event) => !!event.target.closest('[data-listings-for-you-container]');
const isCallToAction = (event) => !!event.target.closest('[data-lead-generator-opener]');
const getStandardCardElement = (event) => event.target.closest('[data-card]');
const isCommunityStandardCard = (dataset) => dataset.type === HousingProductType.Community().getName();
const isPlanStandardCard = (dataset) => dataset.type === HousingProductType.Plan().getName();
const isSpecStandardCard = (dataset) => dataset.type === HousingProductType.Spec().getName();
const isStandardCard = (event) => !!event.target.closest('[data-standard-card]');
const isLinkableListingElement = (event) => event.target instanceof HTMLAnchorElement;
const isFavoriteIcon = (event) => !!event.target.closest('.icon__save');

/* Observable that listens for click events on the document.body */
const bodyClick$ = fromEvent(document.body, 'click');
const listingForYouSectionClick$ =  bodyClick$.pipe(filter(event => isListingForYouSection(event)));
const onWindowLoad$ = fromEvent(window, 'load');

/**
 * Dispatches a Segment Event When the call to action is clicked.
 * @param customProperties - is the custom properties of the event.
 */
const triggerLeadButtonClickedEvent = function(customProperties) {
  store.dispatch(segmentTrack({ eventName: 'Lead Button Clicked', customProperties }));
}

/**
 * Dispatches a Segment Event When the submitt button is clicked.
 * @param customProperties - is the custom properties of the event.
 */
const triggerModalSubmittedEvent = function(customProperties) {
  store.dispatch(segmentTrack({ eventName: 'Lead Modal Form Submitted', customProperties }));
}

const triggerIdentifyModalSubmission = () => {
  store.dispatch(segmentIdentify({ eventName: 'Lead Modal Form Submitted' }));
};

/**
 * Dispatches a Segment Event When a listing is clicked.
 * @param data - listing information needed.
 */
const triggerListingClick = function(data) {
  const customProperties = {
      listing_id: data.card.dataset.id,
      interaction_type: data.interactionType,
      listing_name : data.card.dataset.name
  };

  store.dispatch(segmentTrack({ eventName: 'Listing_Interacted', customProperties }));
};

/**
 * triggerPaidPlanModalSubmitted: Triggers the event when the modal form is submitted.
 * This event only applies for the following forms:
 * - Free Brochure
 * - Get More Information
 * @param {object} modalInformation The information related to the modal opened.
 */
function triggerPaidPlanModalSubmitted(modalInformation) {
  const { listingInformation } = modalInformation;
  const customProperties = {
    builder_id: listingInformation.builderId,
    builder_name: listingInformation.brandName,
    button_action: modalInformation.buttonAction,
    city: listingInformation.city,
    community_id: listingInformation.id,
    community_name: listingInformation.communityName,
    home_price: listingInformation.price,
    home_type: listingInformation.homeStatus,
    market_id: listingInformation.marketId,
    market_name: listingInformation.marketName,
    plan_id: listingInformation.planId,
    primary_image_url: listingInformation.imageUrl,
    state: listingInformation.stateAbbreviation,
    zip: listingInformation.zip,
  };

  return customProperties;
}

/**
 * triggerPaidSpecModalSubmitted: Triggers the event when the modal form is submitted.
 * This event only applies for the following forms:
 * - Free Brochure
 * - Get More Information
 * @param {object} modalInformation The information related to the modal opened.
 */
function triggerPaidSpecModalSubmitted(modalInformation) {
  const { listingInformation } = modalInformation;
  const customProperties = {
    builder_id: listingInformation.builderId,
    builder_name: listingInformation.brandName,
    button_action: modalInformation.buttonAction,
    city: listingInformation.city,
    community_id: listingInformation.id,
    community_name: listingInformation.communityName,
    home_price: listingInformation.price,
    home_type: listingInformation.homeStatus,
    market_id: listingInformation.marketId,
    market_name: listingInformation.marketName,
    primary_image_url: listingInformation.imageUrl,
    state: listingInformation.stateAbbreviation,
    spec_id: listingInformation.specId,
    zip: listingInformation.zip,
  };

  return customProperties;
}

/**
 * triggerPaidCommunityModalSubmitted: Triggers the event when the modal form is submitted.
 * This event only applies for the following forms:
 * - Free Brochure
 * - Get More Information
 * @param {object} modalInformation The information related to the modal opened.
 */
function triggerPaidCommunityModalSubmitted(modalInformation) {
  const { listingInformation } = modalInformation;
  const customProperties = {
    market_id: listingInformation.marketId,
    market_name: listingInformation.marketName,
    builder_id: listingInformation.builderId,
    builder_name: listingInformation.brandName,
    community_id: listingInformation.id,
    community_name: listingInformation.communityName,
    community_status: listingInformation.marketingStatusLabel,
    primary_image_url: listingInformation.imageUrl,
    community_low_price: listingInformation.priceLow,
    community_high_price: listingInformation.priceHigh,
    city: listingInformation.city,
    state: listingInformation.stateAbbreviation,
    zip: listingInformation.zip,
    button_action: modalInformation.buttonAction,
  };

  return customProperties;
}

/**
 * The getCardImageUrls function takes a cardElement as an argument and returns an object with the following properties:
 * primaryImage, secondaryImage, terciaryImage.
 * if there are no images in the cardElement then each property will be assigned to an empty string.
 * This can be used to other events in the list view section.
 * @param cardElement Get the image elements from the card element
 *
 * @return An object with primaryImage, secondaryImage, terciaryImage.
 */
const getCardImageUrls = (cardElement) => {
  const imageElements = cardElement.querySelectorAll('img');

  if (imageElements) {
    return {
      primaryImage: imageElements[0]?.src || '',
      secondaryImage: imageElements[1]?.src || '',
      terciaryImage: imageElements[2]?.src || '',
    };
  }

  return {};
};

/**
 * getLeadButtonClickedPaidCommunityCustomProperties: function to get the custom properties of lead button click
 * based on the community listing card information
 * @param {HTMLElement} standardCommunityCard dom element of the listing card
 * @returns custom properties object
 */
const getLeadButtonClickedPaidCommunityCustomProperties = function(standardCommunityCard){

  const leadButton = standardCommunityCard.querySelector('[data-lead-generator-opener]');
  const leadButtonDataset = leadButton.dataset || {};
  const cardDataset = standardCommunityCard.dataset;

  const customProperties = {
      market_id: cardDataset.marketId,
      market_name: cardDataset.marketName,
      city: cardDataset.city,
      state: cardDataset.stateAbbreviation,
      zip: cardDataset.zip,
      builder_id: cardDataset.builderId,
      builder_name: cardDataset.brandName,
      community_id: cardDataset.id,
      community_name: cardDataset.communityName,
      community_status: cardDataset.marketingStatusLabel,
      primary_image_url: cardDataset.imageUrl,
      community_low_price: cardDataset.priceLow,
      community_high_price: cardDataset.priceHigh,
      button_action: leadButtonDataset.buttonAction,
      page_type: 'Learning_Center',
    };

  return customProperties;
}

/**
* getLeadButtonClickedCallToActionPaidPlanCustomProperties: function to get the custom properties of lead button click
* based on the plan listing card information
* @param {HTMLElement} standardPlanCard dom element of the listing card
* @returns custom properties object
*/
const getLeadButtonClickedPaidPlanCustomProperties = function(standardPlanCard){

  const leadButton = standardPlanCard.querySelector('[data-lead-generator-opener]');
  const leadButtonDataset = leadButton.dataset || {};
  const cardDataset = standardPlanCard.dataset;

  const primaryImageUrl =
    cardDataset.imageUrl !== '' ? cardDataset.imageUrl : getCardImageUrls(standardPlanCard).primaryImage;

  const customProperties = {
    market_id: cardDataset.marketId,
    market_name: cardDataset.marketName,
    city: cardDataset.city,
    state: cardDataset.stateAbbreviation,
    zip: cardDataset.zip,
    builder_id: cardDataset.builderId,
    builder_name: cardDataset.brandName,
    community_id: cardDataset.communityId,
    community_name: cardDataset.communityName,
    primary_image_url: primaryImageUrl,
    button_action: leadButtonDataset.buttonAction,
    plan_id: cardDataset.planId,
    home_type: cardDataset.homeStatus,
    home_price: cardDataset.price,
    page_type: 'Learning_Center',
  };

  return customProperties;
}

/**
* getLeadButtonClickedCallToActionPaidSpecCustomProperties: function to get the custom properties of lead button click
* based on the spec listing card information
* @param {HTMLElement} standardSpecCard dom element of the listing card
* @returns custom properties object
*/
const getLeadButtonClickedPaidSpecCustomProperties = function(standardSpecCard){
  const leadButton = standardSpecCard.querySelector('[data-lead-generator-opener]');
  const leadButtonDataset = leadButton.dataset || {};
  const cardDataset = standardSpecCard.dataset;

  const primaryImageUrl =
    cardDataset.imageUrl !== '' ? cardDataset.imageUrl : getCardImageUrls(standardSpecCard).primaryImage;

  const customProperties = {
    market_id: cardDataset.marketId,
    market_name: cardDataset.marketName,
    city: cardDataset.city,
    state: cardDataset.stateAbbreviation,
    zip: cardDataset.zip,
    builder_id: cardDataset.builderId,
    builder_name: cardDataset.brandName,
    community_id: cardDataset.communityId,
    community_name: cardDataset.communityName,
    primary_image_url: primaryImageUrl,
    button_action: leadButtonDataset.buttonAction,
    spec_id: cardDataset.specId,
    home_type: cardDataset.homeStatus,
    home_price: cardDataset.price,
    page_type: 'Learning_Center',
  };

  return customProperties;
}

/**
 * Dispatch the page view on window load.
 */
const triggerPageViewEvent = () =>
  store.dispatch(
    segmentPageView({
      customProperties: {
        content_type: 'article',
      },
    })
  );

onWindowLoad$.subscribe(triggerPageViewEvent);

/* An observable stream that listens for click events. It then maps each click event to the closest
ancestor element of the clicked target that has a `data-breadcrumb-item` attribute. 
The `filter(Boolean)` function is used to filter out any falsy values. */
const onBreadcrumbClick$ = bodyClick$.pipe(
  map((event) => event.target?.closest('[data-breadcrumb-item]')),
  filter(Boolean)
);

/**
 * Dispatches a Google Analytics 4 event when a breadcrumb link is clicked.
 * @param breadcrumbLink - is the link element that represents a
 * breadcrumb item in a website's navigation.
 */
const triggerBreadcrumbClickEvent = (breadcrumbLink) => {
  const breadcrumbIndex = breadcrumbItems.findIndex((item) => item === breadcrumbLink);
  const customProperties = {
    breadcrumb_position: breadcrumbIndex + 1,
    destination_url: breadcrumbLink.href,
    link_text: breadcrumbLink.innerText,
  };

  store.dispatch(segmentTrack({ eventName: 'Breadcrumb_clicked', customProperties }));
};

onBreadcrumbClick$.subscribe(triggerBreadcrumbClickEvent);

/* An observable stream that listens for click events. It then maps each click event to the closest
ancestor element of the clicked target that has a `data-social-media` attribute. 
The `filter(Boolean)` function is used to filter out any falsy values. */
const onSocialLinkClick$ = bodyClick$.pipe(
  map((event) => event.target?.closest('[data-social-media]')),
  filter(Boolean)
);

/**
 * Dispatches a Google Analytics 4 event when a social link is clicked.
 * @param socialShareLink - is the link element that represents a social link.
 */
const triggerSocialShareClickEvent = (socialShareLink) => {
  const customProperties = {
    share_method: 'button',
    article_length: 'medium',
    platform: socialShareLink.dataset.socialMedia,
  };

  store.dispatch(segmentTrack({ eventName: 'Social_share', customProperties }));
};

onSocialLinkClick$.subscribe(triggerSocialShareClickEvent);

/**
 * Dispatches a Google Analytics 4 event when a search is done.
 * @param suggestion - is the suggestion element that represents the element clicked
 * in the typeahead.
 */
const triggerSuggestionGeolocationEvent = (suggestion) => {
  const customProperties = {
    search_query: suggestion.dataset.value,
    search_location: 'article_page',
  };

  store.dispatch(segmentTrack({ eventName: 'Search_Suggestion_Clicked', customProperties }));
};

typeahead$.subscribe(triggerSuggestionGeolocationEvent);

const triggerSearchBoxButtonClickedEvent = (inputSearchBoxValue) => {
  const customProperties = {
    search_query: inputSearchBoxValue,
    search_location: 'article_page',
  };

  store.dispatch(segmentTrack({ eventName: 'Search_Button_Clicked', customProperties }));
};

const searchBoxButtonClicked$ = fromEvent(searchSubmitElement, 'click').pipe(
  filter((event) => event.target?.matches('[data-search-submit]')),
  map(() => inputSearchBox.value)
);

searchBoxButtonClicked$.subscribe(triggerSearchBoxButtonClickedEvent);

/**
 * Dispatches a Google Analytics 4 event recommendation article is clicked.
 * @param article - is the article clicked in recommendation articles section.
 */
const triggerRecomendedArticlesClick = (article) => {
  const customProperties = {
    recommendation_type: 'similar',
    content_id: article.dataset.slug,
    content_title: article.dataset.title
  };

  store.dispatch(segmentTrack({ eventName: 'Recommendation_clicked', customProperties }));
};

const onRecomendedArticlesClick$ = bodyClick$.pipe(
  filter((event) => event.target.closest('[data-section-name="recommendedArticles"]')),
  map((event) => event.target.closest('[data-link]')),
  filter(Boolean)
);

onRecomendedArticlesClick$.subscribe(triggerRecomendedArticlesClick);

const triggerOnVideoInteractionEvent = ({ target }) => {
  const customProperties = {
    video_length: target.getDuration(),
    watch_time: target.getCurrentTime(),
    video_id: target.getVideoData().video_id,
  };

  store.dispatch(segmentTrack({ eventName: 'Video_Interaction', customProperties }));
};

const attachVideosSegmentEvents = (player) => {
  player.onPause$.subscribe(triggerOnVideoInteractionEvent);
  player.onPlay$.subscribe(triggerOnVideoInteractionEvent);
  player.onEnd$.subscribe(triggerOnVideoInteractionEvent);
};


const youtubeHandlerDownloaded = function(event){
  const videosSubjectObserver$ = event.detail.videosSubjectObserver;
  videosSubjectObserver$.subscribe(attachVideosSegmentEvents);
}

const youtubeHandlerDownloaded$ = fromEvent(document.body, 'youtubeHandlerDownloaded');
youtubeHandlerDownloaded$.subscribe(youtubeHandlerDownloaded);

// Observers to Lead Button Clicked Event Logic
const callToActionStandardCardClick$ = listingForYouSectionClick$.pipe(
  filter(isCallToAction),
  map(getStandardCardElement),
  filter(Boolean),
);
const callToActionSpecClick$ = callToActionStandardCardClick$.pipe(
  filter((standardCard) => isSpecStandardCard(standardCard.dataset)),
  map(getLeadButtonClickedPaidSpecCustomProperties)
);

const callToActionPlanClick$ = callToActionStandardCardClick$.pipe(
  filter((standardCard) => isPlanStandardCard(standardCard.dataset)),
  map(getLeadButtonClickedPaidPlanCustomProperties)
);

const callToActionCommunityClick$ = callToActionStandardCardClick$.pipe(
  filter((standardCard) => isCommunityStandardCard(standardCard.dataset)),
  map(getLeadButtonClickedPaidCommunityCustomProperties)
);

merge(
  callToActionSpecClick$,callToActionPlanClick$,callToActionCommunityClick$
).subscribe(triggerLeadButtonClickedEvent);

// Observers to Listing For You Section Events Logic
const cardClick$ = listingForYouSectionClick$.pipe(
  filter(event => isStandardCard(event) && !isFavoriteIcon(event) &&
  !isLinkableListingElement(event) && !isCallToAction(event)),
  map(event => ({
      interactionType: 'card_click',
      card: getStandardCardElement(event),
  }))
);

const favoriteIconClick$ = listingForYouSectionClick$.pipe(
  filter(event => isFavoriteIcon(event)),
  map(event => ({
      interactionType: 'favorite',
      card: getStandardCardElement(event),
  }))
);

const linkableListingElementClick$ = listingForYouSectionClick$.pipe(
  filter(event => isLinkableListingElement(event)),
  map(event => ({
      interactionType: 'link_click',
      card: getStandardCardElement(event),
  }))
);

merge(cardClick$,favoriteIconClick$,linkableListingElementClick$).subscribe(triggerListingClick);

// Observers to ModalSubmitted Event Logic
const modalSubmittedClick$ = store$.pipe(
  filter(selectFetchingModal),
  map(selectModal),
  filter(Boolean),
  filter((modalInformation) => modalInformation.name === 'leadGenerator'),
);

const modalSubmittedSpecClick$ = modalSubmittedClick$.pipe(
  filter((modalInformation) => isSpecStandardCard(modalInformation.listingInformation)),
  map(triggerPaidSpecModalSubmitted)
);

const modalSubmittedPlanClick$ = modalSubmittedClick$.pipe(
  filter((modalInformation) => isPlanStandardCard(modalInformation.listingInformation)),
  map(triggerPaidPlanModalSubmitted)
);

const modalSubmittedCommunityClick$ = modalSubmittedClick$.pipe(
  filter((modalInformation) => isCommunityStandardCard(modalInformation.listingInformation)),
  map(triggerPaidCommunityModalSubmitted)
);

merge(
  modalSubmittedSpecClick$,modalSubmittedPlanClick$,modalSubmittedCommunityClick$
).subscribe((customProperties) => {
  triggerModalSubmittedEvent(customProperties)
  triggerIdentifyModalSubmission()
});



